import React, { useEffect, useState } from "react";
import { Box, Fab, CircularProgress } from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import CreateApi from "../../Settings/appSettings";
import { useDataContext } from "../../Context/useDataContext";
import { useLSContext } from "../../Context/useLSContext";
import { fetchLeaderboardData } from "../Functions/fetchLeaderboardData";
import { toIdString } from "../../utils/courseDataHelpers";
import { EventSelector } from "../Components/EventSelector";
import { LeaderboardDisplay } from "@moment-golf/leaderboard";

interface LeaderboardProps {
    availableEvents: any;
}

export const Leaderboard = ({ availableEvents }: LeaderboardProps) => {
    const api = CreateApi();

    const {
        isHeadless,
        hideSelector,
        factor,
        appTheme,
        windowSize,
        isPWA,
        setToggleScoring,
    } = useLSContext();

    const {
        eventData,
        setEventData,
        leaderboardData,
        currentRound,
        setCurrentRound,
        setLeaderboardData,
        leaderboardLastQuery,
        setLeaderboardLastQuery,
        leaderboardCourseData,
        setLeaderboardCourseData,
    } = useDataContext();

    const [leaderboardRefreshing, setLeaderboardRefreshing] = useState(false);
    const [seriesPoints, setSeriesPoints] = useState<any>(undefined);
    const appBarHeightPx = windowSize.appBarHeight ?? 60;

    // Tell LSContext the leaderboard tab is active
    useEffect(() => {
        const id = setTimeout(() => setToggleScoring(true), 100);
        return () => clearTimeout(id);
    }, []);

    // Date-bounded polling — fetch every 60 s only while the event is active
    useEffect(() => {
        if (!eventData?.startDate || !eventData?.endDate) return;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = new Date(eventData.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(eventData.endDate);
        end.setHours(0, 0, 0, 0);

        if (today >= start && today <= end) {
            const interval = setInterval(handleFetchLeaderboardData, 60000);
            return () => clearInterval(interval);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventData?._id, eventData?.startDate, eventData?.endDate]);

    // Trigger an immediate fetch if cached data is stale (> 1 min old)
    useEffect(() => {
        if (new Date().getTime() > leaderboardLastQuery.getTime() + 60000 && eventData?._id) {
            handleFetchLeaderboardData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaderboardLastQuery, eventData?._id]);

    // Reset and fetch fresh on event change
    useEffect(() => {
        if (!eventData?._id) return;
        setCurrentRound(1);
        handleFetchLeaderboardData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventData?._id]);

    // Fetch series points when eventGroupIDs are available (populated after eventInfo merges in)
    useEffect(() => {
        const groupIDs: string[] = eventData?.eventGroupIDs ?? [];
        if (groupIDs.length === 0) {
            setSeriesPoints(undefined);
            return;
        }
        api.get('/lb/GetGroupSeriesPoints', { params: { _id: groupIDs[0] } })
            .then((res: any) => setSeriesPoints(res.data?.seriesPoints ?? undefined))
            .catch(() => setSeriesPoints(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventData?._id, eventData?.eventGroupIDs]);

    const handleFetchLeaderboardData = async (roundOverride?: number) => {
        const eventID = toIdString(eventData?._id);
        if (!eventID) return;

        const result = await fetchLeaderboardData({
            api,
            eventID,
            refDate: leaderboardLastQuery,
            currentLeaderboardData: leaderboardData,
        });

        if (result.eventInfo) {
            setEventData((prev: any) => ({ ...prev, ...result.eventInfo }));
        }
        setLeaderboardLastQuery(new Date());
        setLeaderboardData(result.updatedLeaderboardData);
        setLeaderboardCourseData(result.leaderBoardCourseData ?? []);
    };

    // EventSelector is a leaderboard-app–specific component that switches events.
    // It is injected into the display wrapper via headerSlot.
    const showSelector =
        !hideSelector && ((windowSize.isMobile || windowSize.verticalMobile) || isHeadless);

    const headerSlot = showSelector ? (
        <EventSelector
            availableEvents={availableEvents}
            eventData={eventData}
            variant="outlined"
            color="primary.main"
            adornmentOffset={60}
            sx={{ padding: 1, marginTop: `${appBarHeightPx}px` }}
        />
    ) : undefined;

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                zIndex: 0,
                overscrollBehavior: 'none',
                backgroundColor: 'sepia.background',
            }}
            className="live-scoring"
        >
            <LeaderboardDisplay
                leaderboardData={leaderboardData}
                courseData={leaderboardCourseData}
                eventData={eventData}
                currentRound={currentRound}
                availableEvents={availableEvents}
                factor={factor}
                appTheme={appTheme}
                windowSize={windowSize}
                isPWA={isPWA}
                headless={isHeadless}
                hideSelector={hideSelector}
                headerSlot={headerSlot}
                seriesPoints={seriesPoints}
            />
            <Fab
                size="small"
                color="primary"
                aria-label="Refresh leaderboard"
                disabled={leaderboardRefreshing || !eventData?._id}
                onClick={async () => {
                    if (!eventData?._id || leaderboardRefreshing) return;
                    setLeaderboardRefreshing(true);
                    try {
                        await handleFetchLeaderboardData();
                    } finally {
                        setLeaderboardRefreshing(false);
                    }
                }}
                sx={{
                    position: "fixed",
                    top: `calc(${appBarHeightPx}px + 8px)`,
                    right: `max(12px, env(safe-area-inset-right, 0px))`,
                    zIndex: 150,
                }}
            >
                {leaderboardRefreshing ? (
                    <CircularProgress color="inherit" size={22} />
                ) : (
                    <Refresh />
                )}
            </Fab>
        </Box>
    );
};
